import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-3">
            文章未找到
          </h1>
          <Link
            to="/"
            className="text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article.slug, article.category, 3);
  const canonicalUrl = `${window.location.origin}${__BASE_PATH__}/article/${slug}`;

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          url: canonicalUrl,
          datePublished: article.date,
          inLanguage: 'zh-CN',
          author: {
            '@type': 'Organization',
            name: '大胸姐妹指南',
          },
          publisher: {
            '@type': 'Organization',
            name: '大胸姐妹指南',
          },
          image: article.imageUrl,
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  ...(category
                    ? [{ label: category.name, to: `/category/${category.slug}` }]
                    : []),
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <article>
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span>{article.date}</span>
                  <span>{article.readTime}阅读</span>
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {category.name}
                    </Link>
                  )}
                </div>
              </div>

              <div className="w-full h-[300px] md:h-[400px] rounded-lg overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-strong:text-foreground-900 prose-h2:text-lg md:prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-8 prose-h2:mb-4"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </article>

            {relatedArticles.length > 0 && (
              <div className="mt-14 border-t border-background-200/70 pt-10">
                <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-6">
                  相关文章推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <ArticleCard key={related.slug} article={related} />
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 text-center">
              <Link
                to={category ? `/category/${category.slug}` : '/'}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-secondary-100 text-secondary-700 rounded-full text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回{category ? category.name : '首页'}
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}